import React, {
  useState,
  useCallback,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import moment from 'moment';
import format from 'string-template';
import { IconButton } from '@mui/material';

import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import useToast from '../../../../hooks/useToast';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import AnnouncementComment from '../../../../Model/AnnouncementComment';
import AvatarSizeType from '../../../../../components/Avatar/AvatarSizes';
import LinkifiedText from '../../../../../components/LinkifiedText';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import AttachmentSlider from '../../../../components/AttachmentSlider';
import LikeButton from '../LikeButton';

import {
  Container,
  DateText,
  CommentText,
  CommentAttachmentsWrapper,
  HeaderContainer,
  StyledUserProfileCard,
  BottomRow,
  StyledRemoveIcon,
  StyledEditIcon,
  ActionButtonContainer,
} from './styles';

import texts from './texts.json';

const COMMENT_LENGTH_IN_CONFIRM_DIALOG = 70;

const CommentItem = ({ comment, onClickEditComment }) => {
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const { userId: loggedInUserId } = useContext(LoggedInUserContext);
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  const {
    userName,
    userAvatarUrl,
    comment: commentText,
    reactions,
    userId,
    isByCoach,
    displayDate,
    attachments,
  } = comment;

  const onRemoveComment = useCallback(async () => {
    await comment.remove(loggedInUserId);
    if (isComponentMountedRef.current) {
      setIsConfirmDialogOpen(false);
    }
    showToast(texts.removeCommentSuccess);
  }, [
    comment,
    showToast,
    isComponentMountedRef,
    loggedInUserId,
  ]);

  return (
    <Container id={comment.id}>
      <HeaderContainer>
        <StyledUserProfileCard
          dataRow={{
            userName,
            userAvatar: userAvatarUrl,
          }}
          clientId={userId}
          hideActions
          avatarSize={AvatarSizeType.SMALL}
        />
        <DateText>{moment(displayDate.toDate()).fromNow()}</DateText>
      </HeaderContainer>
      <CommentText><LinkifiedText text={commentText} /></CommentText>
      {attachments?.length > 0 && (
        <CommentAttachmentsWrapper>
          <AttachmentSlider
            attachments={attachments}
            maxHeight="140px"
          />
        </CommentAttachmentsWrapper>
      )}
      <BottomRow>
        <LikeButton
          announcementId={comment.announcementId}
          commentId={comment.id}
          count={reactions}
        />
        <ActionButtonContainer>
          {isByCoach && (
            <IconButton onClick={() => onClickEditComment(comment)}>
              <StyledEditIcon />
            </IconButton>
          )}
          <IconButton onClick={() => setIsConfirmDialogOpen(true)}>
            <StyledRemoveIcon />
          </IconButton>
        </ActionButtonContainer>
      </BottomRow>
      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        onCancel={() => setIsConfirmDialogOpen(false)}
        onConfirm={onRemoveComment}
        dialogTexts={{
          title: texts.removeComment,
          content: format(texts.removeCommentMessage, {
            comment: commentText.length > COMMENT_LENGTH_IN_CONFIRM_DIALOG
              ? `${commentText.slice(0, COMMENT_LENGTH_IN_CONFIRM_DIALOG)}…`
              : commentText,
          }),
        }}
      />
    </Container>
  );
};

CommentItem.propTypes = {
  comment: PropTypes.instanceOf(AnnouncementComment).isRequired,
  onClickEditComment: PropTypes.func.isRequired,
};

export default compose(
  observer,
)(CommentItem);
